import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { ToastService } from '@app/core/services/toast.service';
import { AuthService } from '@app/core/services/auth.service';
import { isLoginUrl, loginUrlTree } from '@app/core/utils';

/**
 * CW-13d-admin — 401 handling in cookie-auth mode.
 *
 * A 401 means the `tb_admin_access` cookie is missing or expired. Admin has
 * no refresh token by design (CW-13a), so we clear the in-memory session,
 * surface a session-expired toast, and navigate to /auth/login. We do NOT
 * attempt any silent refresh round-trip.
 *
 * The current page travels as `returnUrl` so the admin resumes where they
 * were after signing back in. When already on the login page (e.g. several
 * requests 401 in parallel), we stay put rather than overwrite the
 * `returnUrl` the first redirect set.
 *
 * The session probe (`GET /admin/auth/me`) is exempt: `authGuard` handles
 * its failure and redirects with the target URL. Redirecting here as well
 * would cancel that in-flight navigation and drop the `returnUrl` — and a
 * cold visit with no cookie is not an "expired" session worth a toast.
 *
 * 403 keeps its existing behaviour (toast, no redirect).
 * Other status codes are rethrown for the caller to handle.
 */
const SESSION_PROBE_PATH = '/admin/auth/me';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const toastService = inject(ToastService);
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((error: { status: number; message: string }) => {
      const isSessionProbe = req.url.split('?')[0].endsWith(SESSION_PROBE_PATH);

      if (error.status === 401 && !isSessionProbe) {
        authService.clearSession();
        toastService.error('Session expired', 'Please log in again');
        if (!isLoginUrl(router.url)) {
          router.navigateByUrl(loginUrlTree(router, router.url));
        }
      }

      if (error.status === 403) {
        toastService.error('Access denied', 'You do not have permission to perform this action.');
      }

      return throwError(() => error);
    }),
  );
};
